import React, { useRef, useState } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ColorRing } from 'react-loader-spinner';
import DefaultLayout from '../reusableComponents/defaultLayout';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountHeader, { subContractorTabs } from './account/AccountHeader';
import { API_BASE_URL } from '../config/api';

export default function PostNewProject() {
  const [startDate, setStartDate] = useState(null);
  const [completionDate, setCompletionDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const userRole = useSelector((state) => state?.userProfileSlice?.userData?.data?.role);
  const isSubContractor = userRole === 'sub_contractor';

  const states = [
    "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DE", "DC", "FL",
    "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
    "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
    "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
    "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK"
  ];

  const serviceOptions = [
    { value: 'commercial', label: 'Commercial' },
    { value: 'residential', label: 'Residential' },
    { value: 'federal', label: 'Federal' },
    { value: 'road_construction_and_industrial', label: 'Road & Industrial' },
    { value: 'other', label: 'Other' }
  ];

  const scopeOptions = [
    { value: 'site_preparation', label: 'Site Preparation' },
    { value: 'concrete', label: 'Concrete' },
    { value: 'structural_and_framing', label: 'Structural & Framing' },
    { value: 'roofing_siding_and_sheet_metal_work', label: 'Roofing & Sheet Metal' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'hvac', label: 'HVAC' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'carpentry', label: 'Carpentry' },
    { value: 'drywall', label: 'Drywall' },
    { value: 'painting_and_paper_hanging', label: 'Painting & Wallpaper' },
    { value: 'other', label: 'Other' }
  ];

  const radioOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'tbd', label: 'TBD' },
    { value: 'design_build', label: 'Design Build' }
  ];

  const handleStartDateChange = (date, setFieldValue) => {
    const formattedDate = moment(date.$d).format('YYYY-MM-DD');
    setStartDate(formattedDate);
    setFieldValue("project_start_date", formattedDate);
  };

  const handleCompletionDateChange = (date, setFieldValue) => {
    const formattedDate = moment(date.$d).format('YYYY-MM-DD');
    setCompletionDate(formattedDate);
    setFieldValue("project_end_date", formattedDate);
  };

  const initialValues = {
    project_name: '',
    email: '',
    price: 0,
    project_rep: '',
    phone: '',
    project_description: '',
    past_work: '',
    designer: '',
    engineer: '',
    architect: '',
    permits: '',
    permit_doc: [],
    financing: '',
    plan: '',
    plan_doc: [],
    plan_image: [],
    jobStatus: '',
    project_start_date: null,
    project_end_date: null,
    selectedServices: [],
    scope: [],
    licensedWorkStates: ''
  };

  const validationSchema = Yup.object().shape({
    project_name: Yup.string().min(3, 'Minimum 3 characters').max(40, 'Maximum 40 characters').required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    project_rep: Yup.string().required('Required'),
    phone: Yup.string().matches(/^(\+\d{1,4}\s?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/, 'Invalid format').required('Required'),
    price: Yup.string().required('Required'),
    project_description: Yup.string().required('Required'),
    past_work: Yup.string().required('Required'),
    designer: Yup.string().required('Required'),
    engineer: Yup.string().required('Required'),
    architect: Yup.string().required('Required'),
    permits: Yup.string().required('Required'),
    financing: Yup.string().required('Required'),
    plan: Yup.string().required('Required'),
    selectedServices: Yup.array().min(1, 'Select at least one'),
    scope: Yup.array().min(1, 'Select at least one'),
    licensedWorkStates: Yup.string().required('Required'),
    project_start_date: Yup.string().required('Required'),
    project_end_date: Yup.string().required('Required'),
  });

  const permitDocument = useRef(null);
  const planDocument = useRef(null);
  const planImages = useRef(null);

  const [selectedPlanImages, setSelectedPlanImages] = useState([]);
  const [selectedPermitDoc, setSelectedPermitDoc] = useState([]);
  const [selectedPlanDoc, setSelectedPlanDoc] = useState([]);

  const handlePlanImageChange = (event, setFieldValue) => {
    const selectedImages = event.currentTarget.files;
    setSelectedPlanImages(prev => [...prev, ...selectedImages]);
    setFieldValue("plan_image", selectedImages);
  };

  const handleDeleteImage = (event, index, setFieldValue) => {
    event.preventDefault();
    const updated = [...selectedPlanImages];
    updated.splice(index, 1);
    setSelectedPlanImages(updated);
    setFieldValue("plan_image", updated);
  };

  const handlePermitDocChange = (event, setFieldValue) => {
    const selectedDoc = Array.from(event.currentTarget.files);
    setSelectedPermitDoc(prev => [...prev, ...selectedDoc]);
    setFieldValue("permit_doc", selectedDoc);
  };

  const handleDeletePermitDoc = (event, index, setFieldValue) => {
    event.preventDefault();
    const updated = [...selectedPermitDoc];
    updated.splice(index, 1);
    setSelectedPermitDoc(updated);
    setFieldValue("permit_doc", updated);
  };

  const handlePlanDocChange = (event, setFieldValue) => {
    const selectedDoc = Array.from(event.currentTarget.files);
    setSelectedPlanDoc(prev => [...prev, ...selectedDoc]);
    setFieldValue("plan_doc", selectedDoc);
  };

  const handleDeletePlanDoc = (event, index, setFieldValue) => {
    event.preventDefault();
    const updated = [...selectedPlanDoc];
    updated.splice(index, 1);
    setSelectedPlanDoc(updated);
    setFieldValue("plan_doc", updated);
  };

  const handleSubmit = async (values) => {
    const url = API_BASE_URL;
    values.project_start_date = startDate;
    values.project_end_date = completionDate;

    if (!startDate || startDate === "Invalid date") {
      toast.error('Please select a valid start date.', { autoClose: 3000 });
      return;
    }
    if (!completionDate) {
      toast.error('Please select an end date.', { autoClose: 3000 });
      return;
    }
    if (startDate === completionDate) {
      toast.error('Start and end dates cannot be the same.', { autoClose: 3000 });
      return;
    }
    if (moment(startDate).isAfter(completionDate)) {
      toast.error('Start date must be before end date.', { autoClose: 3000 });
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('authToken');

    if (token) {
      try {
        const formData = new FormData();
        formData.append('project_name', values.project_name);
        formData.append('project_rep', values.project_rep);
        formData.append('email', values.email);
        formData.append('project_state', values.licensedWorkStates);
        formData.append('phone', parseInt(values.phone, 10));
        formData.append('price', parseInt(values.price, 10));
        formData.append('plan', values.plan);
        formData.append('past_work', values.past_work);
        formData.append('designer', values.designer);
        formData.append('architect', values.architect);
        formData.append('engineer', values.engineer);
        formData.append('financing', values.financing);
        formData.append('permits', values.permits);
        formData.append('project_start_date', values.project_start_date);
        formData.append('project_description', values.project_description);
        formData.append('project_end_date', values.project_end_date);
        
        values.selectedServices.forEach(s => formData.append("services[]", s));
        values.scope.forEach(s => formData.append("identify_scope[]", s));
        selectedPlanDoc.forEach(p => formData.append("plan_doc[]", p));
        selectedPlanImages.forEach(i => formData.append("plan_image[]", i));
        selectedPermitDoc.forEach(p => formData.append("permit_doc[]", p));

        const response = await fetch(`${url}/create_project`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        
        const { message, success } = await response.json();
        if (success) {
          toast.success('Project created successfully!', { autoClose: 3000 });
          navigate('/account/1');
        } else {
          toast.error('Error: ' + message, { autoClose: 3000 });
        }
      } catch (error) {
        toast.error('An error occurred: ' + error, { autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    }
  };

  const RadioGroup = ({ name, options = radioOptions }) => (
    <div className="radio_group">
      {options.map(opt => (
        <label key={opt.value} className="radio_item">
          <Field type="radio" name={name} value={opt.value} />
          <span className="radio_label">{opt.label}</span>
        </label>
      ))}
    </div>
  );

  return (
    <DefaultLayout>
      <AccountHeader tabs={isSubContractor ? subContractorTabs : undefined} />
      <div className="profile_page">
        <div className="container">
          <button className="back_link" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" /> Back to Projects
          </button>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form>
                {/* Project Details Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Project Details</h3>
                    <p>Basic information about your project</p>
                  </div>
                  <div className="section_content">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form_group">
                          <label htmlFor="project_name">Project Name</label>
                          <Field type="text" id="project_name" name="project_name" className="form-control" placeholder="Enter project name" />
                          <ErrorMessage name="project_name" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_group">
                          <label htmlFor="price">Base Price</label>
                          <div className="input_with_prefix">
                            <span className="input_prefix">$</span>
                            <Field type="number" id="price" name="price" className="form-control" placeholder="0" />
                          </div>
                          <ErrorMessage name="price" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="form_group">
                          <label htmlFor="project_description">Project Description</label>
                          <Field as="textarea" id="project_description" name="project_description" className="form-control" rows="4" placeholder="Describe the project scope and requirements..." />
                          <ErrorMessage name="project_description" component="div" className="field_error" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Contact Information</h3>
                    <p>Primary contact for this project</p>
                  </div>
                  <div className="section_content">
                    <div className="row">
                      <div className="col-lg-4 col-md-6">
                        <div className="form_group">
                          <label htmlFor="project_rep">Project Representative</label>
                          <Field type="text" id="project_rep" name="project_rep" className="form-control" placeholder="Contact name" />
                          <ErrorMessage name="project_rep" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="form_group">
                          <label htmlFor="email">Email Address</label>
                          <Field type="email" id="email" name="email" className="form-control" placeholder="email@example.com" />
                          <ErrorMessage name="email" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="form_group">
                          <label htmlFor="phone">Phone Number</label>
                          <Field type="text" id="phone" name="phone" className="form-control" placeholder="xxx-xxx-xxxx" />
                          <ErrorMessage name="phone" component="div" className="field_error" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline & Location Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Timeline & Location</h3>
                    <p>Project schedule and state</p>
                  </div>
                  <div className="section_content">
                    <div className="row">
                      <div className="col-lg-4 col-md-6">
                        <div className="form_group">
                          <label>Start Date</label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker 
                              name="project_start_date" 
                              disablePast 
                              value={startDate} 
                              onChange={(date) => handleStartDateChange(date, setFieldValue)}
                              slotProps={{ textField: { fullWidth: true, placeholder: 'Select date' } }}
                            />
                          </LocalizationProvider>
                          <ErrorMessage name="project_start_date" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="form_group">
                          <label>Completion Date</label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker 
                              disabled={!startDate} 
                              disablePast 
                              name="project_end_date" 
                              value={completionDate} 
                              onChange={(date) => handleCompletionDateChange(date, setFieldValue)}
                              slotProps={{ textField: { fullWidth: true, placeholder: 'Select date' } }}
                            />
                          </LocalizationProvider>
                          <ErrorMessage name="project_end_date" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <div className="form_group">
                          <label htmlFor="licensedWorkStates">Project State</label>
                          <Field name="licensedWorkStates" as="select" className="form-control">
                            <option value="" disabled>Select State</option>
                            {states.map((state, idx) => (
                              <option value={state} key={idx}>{state}</option>
                            ))}
                          </Field>
                          <ErrorMessage name="licensedWorkStates" component="div" className="field_error" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Team Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Project Team</h3>
                    <p>Indicate which professionals are involved</p>
                  </div>
                  <div className="section_content">
                    <div className="team_grid">
                      <div className="team_item">
                        <label>Designer</label>
                        <RadioGroup name="designer" />
                        <ErrorMessage name="designer" component="div" className="field_error" />
                      </div>
                      <div className="team_item">
                        <label>Engineer</label>
                        <RadioGroup name="engineer" />
                        <ErrorMessage name="engineer" component="div" className="field_error" />
                      </div>
                      <div className="team_item">
                        <label>Architect</label>
                        <RadioGroup name="architect" />
                        <ErrorMessage name="architect" component="div" className="field_error" />
                      </div>
                      <div className="team_item">
                        <label>Financing</label>
                        <RadioGroup name="financing" />
                        <ErrorMessage name="financing" component="div" className="field_error" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Permits & Plans Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Permits & Plans</h3>
                    <p>Documentation status and uploads</p>
                  </div>
                  <div className="section_content">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form_group">
                          <label>Permits Status</label>
                          <RadioGroup name="permits" />
                          <ErrorMessage name="permits" component="div" className="field_error" />
                        </div>
                        <div className="form_group">
                          <label>Upload Permits (PDF)</label>
                          <div className="file_dropzone compact">
                            <input type="file" name="permit_doc" multiple accept=".pdf" ref={permitDocument} onChange={(e) => handlePermitDocChange(e, setFieldValue)} />
                            <span>Click to upload PDF files</span>
                          </div>
                          {selectedPermitDoc.length > 0 && (
                            <div className="uploaded_files_list">
                              {selectedPermitDoc.map((doc, index) => (
                                <div key={index} className="uploaded_file_item">
                                  <span>{doc.name.length > 25 ? `${doc.name.slice(0, 25)}...` : doc.name}</span>
                                  <button type="button" onClick={(e) => handleDeletePermitDoc(e, index, setFieldValue)}>
                                    <CloseIcon fontSize="small" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_group">
                          <label>Plans Available</label>
                          <RadioGroup name="plan" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} />
                          <ErrorMessage name="plan" component="div" className="field_error" />
                        </div>
                        <div className="form_group">
                          <label>Upload Plans (PDF)</label>
                          <div className="file_dropzone compact">
                            <input type="file" name="plan_doc" multiple accept=".pdf" ref={planDocument} onChange={(e) => handlePlanDocChange(e, setFieldValue)} />
                            <span>Click to upload PDF files</span>
                          </div>
                          {selectedPlanDoc.length > 0 && (
                            <div className="uploaded_files_list">
                              {selectedPlanDoc.map((doc, index) => (
                                <div key={index} className="uploaded_file_item">
                                  <span>{doc.name.length > 25 ? `${doc.name.slice(0, 25)}...` : doc.name}</span>
                                  <button type="button" onClick={(e) => handleDeletePlanDoc(e, index, setFieldValue)}>
                                    <CloseIcon fontSize="small" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="form_group mt-4">
                      <label>Project Images</label>
                      <div className="file_dropzone compact">
                        <input type="file" multiple name="plan_image" ref={planImages} accept=".jpg,.jpeg,.png" onChange={(e) => {
                          if (e.currentTarget.files[0] && /^image\/(jpeg|jpg|png)$/.test(e.currentTarget.files[0].type)) {
                            handlePlanImageChange(e, setFieldValue);
                          }
                        }} />
                        <span>Click to upload images (JPG, PNG)</span>
                      </div>
                      {selectedPlanImages.length > 0 && (
                        <div className="uploaded_images_grid">
                          {selectedPlanImages.map((image, index) => (
                            <div key={index} className="uploaded_image_item">
                              <img src={URL.createObjectURL(image)} alt={`Preview ${index}`} />
                              <button type="button" onClick={(e) => handleDeleteImage(e, index, setFieldValue)}>
                                <CloseIcon fontSize="small" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Past Work Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Background</h3>
                    <p>Previous work relationships</p>
                  </div>
                  <div className="section_content">
                    <div className="form_group">
                      <label htmlFor="past_work">Who have you worked with in the past? (Banks, Lenders, etc.)</label>
                      <Field as="textarea" id="past_work" name="past_work" className="form-control" rows="3" placeholder="List previous partners, lenders, or contractors..." />
                      <ErrorMessage name="past_work" component="div" className="field_error" />
                    </div>
                  </div>
                </div>

                {/* Services & Scope Section */}
                <div className="profile_section">
                  <div className="section_header">
                    <h3>Services & Scope</h3>
                    <p>Select the services and scope needed for this project</p>
                  </div>
                  <div className="section_content">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="checkbox_group">
                          <h4>Service Types</h4>
                          <div className="checkbox_grid">
                            {serviceOptions.map((service) => (
                              <label key={service.value} className="checkbox_item">
                                <Field type="checkbox" name="selectedServices" value={service.value} />
                                <span className="checkmark"></span>
                                <span className="checkbox_label">{service.label}</span>
                              </label>
                            ))}
                          </div>
                          <ErrorMessage name="selectedServices" component="div" className="field_error" />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="checkbox_group">
                          <h4>Work Scope</h4>
                          <div className="checkbox_grid two_columns">
                            {scopeOptions.map((scope) => (
                              <label key={scope.value} className="checkbox_item">
                                <Field type="checkbox" name="scope" value={scope.value} />
                                <span className="checkmark"></span>
                                <span className="checkbox_label">{scope.label}</span>
                              </label>
                            ))}
                          </div>
                          <ErrorMessage name="scope" component="div" className="field_error" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="profile_actions">
                  <button type="button" className="cancel_btn" onClick={() => navigate(-1)}>Cancel</button>
                  <button type="submit" disabled={loading} className="save_btn">
                    {loading ? (
                      <>
                        <ColorRing visible={true} height="24" width="24" colors={['#fff', '#fff', '#fff', '#fff', '#fff']} />
                        Creating...
                      </>
                    ) : 'Create Project'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </DefaultLayout>
  );
}
